import React, { useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/api/client";
import { Phone, Mail, MapPin } from "lucide-react";
import { trackCall } from "@/lib/track";

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", {
        name: form.name,
        phone: form.phone,
        email: form.email,
        message: form.message,
        source: "contact_form",
      });
      toast.success("Thanks! We'll be in touch shortly.");
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send. Please call us instead.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-card/40 border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14">
        <div>
          <div className="text-xs uppercase tracking-widest text-primary font-bold">04 / Contact</div>
          <h2 className="mt-4 text-4xl md:text-5xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>
            {t.contact.title}
          </h2>
          <p className="mt-4 text-muted-foreground text-lg">{t.contact.subtitle}</p>

          <div className="mt-10 space-y-6">
            <a href="tel:+13316459072" onClick={() => trackCall("contact_section")} className="flex items-center gap-4 group" data-testid="contact-phone">
              <div className="h-12 w-12 rounded-lg bg-primary/10 border border-primary/30 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                <Phone className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Phone</div>
                <div className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>{t.contact.phone}</div>
              </div>
            </a>

            <a href="mailto:floresonthemovee@gmail.com" className="flex items-center gap-4 group" data-testid="contact-email">
              <div className="h-12 w-12 rounded-lg bg-primary/10 border border-primary/30 flex items-center justify-center text-primary">
                <Mail className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Email</div>
                <div className="text-lg font-semibold">{t.contact.email}</div>
              </div>
            </a>

            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-primary/10 border border-primary/30 flex items-center justify-center text-primary">
                <MapPin className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Service area</div>
                <div className="text-lg font-semibold">{t.contact.area}</div>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="rounded-2xl border border-border bg-background/60 backdrop-blur p-6 md:p-8 space-y-4" data-testid="contact-form">
          <Input
            placeholder={t.contact.form.name}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            className="bg-card"
            data-testid="contact-input-name"
          />
          <div className="grid sm:grid-cols-2 gap-4">
            <Input
              placeholder={t.contact.form.phone}
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              required
              className="bg-card"
              data-testid="contact-input-phone"
            />
            <Input
              type="email"
              placeholder={t.contact.form.email}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="bg-card"
              data-testid="contact-input-email"
            />
          </div>
          <Textarea
            placeholder={t.contact.form.message}
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            rows={5}
            className="bg-card"
            data-testid="contact-input-message"
          />
          <Button
            type="submit"
            disabled={submitting}
            className="w-full rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold py-6 text-base"
            data-testid="contact-submit-btn"
          >
            {submitting ? "..." : t.contact.form.submit}
          </Button>
        </form>
      </div>
    </section>
  );
}
